import { Box, Grid, Chip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { formatGermanDate } from "../atoms/Utils";
import CouponTableMenu from "./CouponTableMenu";
import CouponFilters from "./CouponFilters";
import { CouponTemplateDto, CouponTemplateStatus, ActivationType } from "../../store/models/coupon/CouponManagementDto";
import { Api } from "../../api/Api";
import { thunkCreateErrorNotification, thunkCreateSuccessNotification } from "../../store/actions/NotificationActions";
import { COLOR_GRAY_3 } from "../atoms/ImsMaterialTheme";

const CouponTable = (props: ThunkProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState(10);
  const [coupons, setCoupons] = useState<any[]>([]);
  const isInitialMount = React.useRef(true);
  const fetchCouponsRef = React.useRef<() => void>(() => {});
  const hasInitializedPageEffect = React.useRef(false);

  const fetchCoupons = useCallback(() => {
    setIsLoading(true);
    Api.getAllCouponTemplates(
      page + 1,
      size,
      undefined,
      undefined,
      statusFilter.length > 0 ? statusFilter : undefined,
      undefined,
      searchTerm || undefined
    )
      .then((data) => {
        setCoupons(data.data || []);
        setTotal(data.total || 0);
      })
      .catch((e) => {
        setCoupons([]);
        props.thunkCreateErrorNotification(t("coupon.table.errorLoading") || "Error loading coupons", e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [props, t, page, size, statusFilter, searchTerm]);

  useEffect(() => {
    fetchCouponsRef.current = fetchCoupons;
  }, [fetchCoupons]);

  // Debounced fetch when searchTerm or statusFilter changes, reset page to 0
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchCouponsRef.current();
      return;
    }

    const timer = setTimeout(() => {
      setPage((prevPage) => {
        if (prevPage === 0) {
          fetchCouponsRef.current();
          return prevPage;
        }
        return 0;
      });
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, statusFilter]);

  // Fetch when page or size changes
  useEffect(() => {
    if (!hasInitializedPageEffect.current) {
      hasInitializedPageEffect.current = true;
      return;
    }

    fetchCouponsRef.current();
  }, [page, size]);

  const transformApiCouponToDto = (apiCoupon: any): CouponTemplateDto => {

    const getValue = () => {
      if (apiCoupon.valueType === 'PERCENTAGE' && apiCoupon.discountPercentage) {
        return `${apiCoupon.discountPercentage}%`;
      }
      if (apiCoupon.valueType === 'FIXED_AMOUNT' && apiCoupon.discountAmount) {
        return `€${apiCoupon.discountAmount}`;
      }
      if (apiCoupon.valueType === 'FREE_SHIPPING') {
        return t("coupon.valueType.free_shipping");
      }
      if (apiCoupon.valueType === 'GIFT') {
        return t("coupon.valueType.gift");
      }
      return '-';
    };

    const getCodeType = () => {
      switch (apiCoupon.codeType) {
        case 'INDIVIDUAL_GENERATED':
          return 'INDIVIDUAL' as any;
        case 'GENERIC':
        default:
          return 'GENERIC' as any;
      }
    };

    const getActivationType = () => {
      return apiCoupon.activationType === 'FREE' ? ActivationType.FREE : ActivationType.POINTS;
    };

    return {
      couponTemplateNumber: apiCoupon.couponTemplateNumber,
      name: apiCoupon.name,
      status: apiCoupon.status as CouponTemplateStatus,
      codeType: getCodeType(),
      activationType: getActivationType(),
      valueType: apiCoupon.valueType,
      value: getValue(),
      validityType: apiCoupon.validityType ||
                    (apiCoupon.validTo || apiCoupon.expirationDate ? 'FIXED_DATE' : 'DAYS_FROM_ACTIVATION'),
      expirationDate: apiCoupon.expirationDate || apiCoupon.validTo,
      validityPeriodDays: apiCoupon.validityPeriodDays,
      requiredPoints: apiCoupon.requiredPoints,
      minimumPurchaseAmount: apiCoupon.minimumPurchaseAmount
    };
  };

  const transformedCoupons: CouponTemplateDto[] = coupons.map(transformApiCouponToDto);

  const getStatusDisplay = (status: CouponTemplateStatus): string => {
    switch (status) {
      case CouponTemplateStatus.ACTIVE:
        return "Active";
      case CouponTemplateStatus.DRAFT:
        return "Draft";
      case CouponTemplateStatus.INACTIVE:
        return "Inactive";
      default:
        return status;
    }
  };

  const getCodeTypeDisplay = (codeType: string): string => {
    switch (codeType?.toUpperCase()) {
      case 'GENERIC':
        return t("coupon.table.genericCodes");
      case 'INDIVIDUAL':
        return t("coupon.table.individualCodes");
      default:
        return codeType;
    }
  };

  const getValueTypeDisplay = (valueType: string): string => {
    switch (valueType?.toUpperCase()) {
      case 'FIXED_AMOUNT':
        return t("coupon.valueType.fixed_amount");
      case 'DISCOUNT_PERCENTAGE':
      case 'PERCENTAGE':
        return t("coupon.valueType.percentage");
      case 'FREE_SHIPPING':
        return t("coupon.valueType.free_shipping");
      case 'GIFT':
        return t("coupon.valueType.gift");
      default:
        return valueType?.replace(/_/g, ' ') || '';
    }
  };

  const getActivationTypeDisplay = (coupon: CouponTemplateDto): string => {
    if (coupon.activationType === ActivationType.FREE) {
      return t("coupon.activationType.free");
    }
    
    if (coupon.activationType === ActivationType.POINTS && coupon.requiredPoints) {
      return `${coupon.requiredPoints} ${t("coupon.table.points")}`;
    }
    
    return coupon.activationType;
  };

  const getNameDisplay = (coupon: CouponTemplateDto): JSX.Element => {
    const codeType = getCodeTypeDisplay(coupon.codeType);
    return (
      <div>
        <div><strong>{coupon.name}</strong></div>
        <div style={{ fontSize: '0.8em', color: '#666' }}>{codeType}</div>
      </div>
    );
  };

  const getTypeDisplay = (coupon: CouponTemplateDto): string => {
    return getValueTypeDisplay(coupon.valueType);
  };

  const getExpiresAfterDisplay = (coupon: CouponTemplateDto): string => {
    if (coupon.validityType === 'FIXED_DATE' && coupon.expirationDate) {
      return formatGermanDate(coupon.expirationDate);
    } else if (coupon.validityType === 'DAYS_FROM_ACTIVATION' && coupon.validityPeriodDays && coupon.validityPeriodDays > 0) {
      return `${coupon.validityPeriodDays} ${t("coupon.table.days")}`;
    }
    return '-';
  };

  // No filtering needed since we don't have a DELETED status
  const filteredCoupons = transformedCoupons;

  const rows = filteredCoupons.map((coupon) => ({
    id: coupon.couponTemplateNumber,
    name: coupon.name,
    codeType: coupon.codeType,
    valueType: coupon.valueType,
    value: coupon.value,
    activationType: coupon.activationType,
    requiredPoints: coupon.requiredPoints,
    validityType: coupon.validityType,
    expirationDate: coupon.expirationDate,
    validityPeriodDays: coupon.validityPeriodDays,
    minimumPurchaseAmount: coupon.minimumPurchaseAmount,
    status: coupon.status,
    originalData: coupon
  }));

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("coupon.table.name"),
      flex: 1,
      minWidth: 350,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => getNameDisplay(params.row.originalData),
    },
    {
      field: "valueType",
      headerName: t("coupon.table.type"),
      flex: 1,
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Chip
            label={getTypeDisplay(params.row.originalData)}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: "value",
      headerName: t("coupon.table.value"),
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "activationType",
      headerName: t("coupon.table.activation"),
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => getActivationTypeDisplay(params.row.originalData),
    },
    {
      field: "expiresAfter",
      headerName: t("coupon.table.expiresAfter"),
      flex: 1,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => getExpiresAfterDisplay(params.row.originalData),
    },
    {
      field: "status",
      headerName: t("coupon.table.status"),
      flex: 1,
      minWidth: 100,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Chip
          label={getStatusDisplay(params.value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 50,
      disableExport: true,
      renderHeader: () => <span></span>,
      renderCell: (params) => <CouponTableMenu coupon={params.row.originalData} onCouponUpdated={fetchCoupons} />,
    },
  ];

  return (
    <React.Fragment>
      <CouponFilters
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        onSearchChange={setSearchTerm}
        onStatusChange={setStatusFilter}
      />
      <Grid>
        <Box
          sx={{
            width: "100%",
            bgcolor: "white",
            boxShadow: 2,
            borderRadius: 2,
            marginBottom: 4,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: COLOR_GRAY_3,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
          }}
        >
          <DataGrid
            disableColumnSelector={true}
            disableSelectionOnClick={true}
            disableVirtualization={true}
            getRowId={(row) => row.id}
            rows={rows}
            rowCount={total}
            loading={isLoading}
            sortingMode="server"
            rowsPerPageOptions={[10, 50, 100]}
            pagination
            page={page}
            pageSize={size}
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setSize(newPageSize)}
            columns={columns}
            autoHeight
            rowHeight={60}
            componentsProps={{
              pagination: {
                labelRowsPerPage: t("common.pagination.rowsPerPage"),
              },
            }}
          />
        </Box>
      </Grid>
    </React.Fragment>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      thunkCreateErrorNotification,
      thunkCreateSuccessNotification,
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(CouponTable);
